import { Box, GridProps } from '@material-ui/core';
import { Country } from '@nemuru-eng/js-api-client';
import { FieldPath, FieldValues, Path } from 'react-hook-form';

import { AutocompleteFormField } from '@/components/shared/components/NewContextForms/AutocompleteFormField2';
import { countryToFlag } from '@/components/shared/components/NewContextForms/PhoneFormDoubleField/utils/countryToFlag';
import { getDialCodeList } from '@/components/shared/components/NewContextForms/PhoneFormDoubleField/utils/getDialCodeList';
import { SelectFormFieldProps } from '@/components/shared/components/NewContextForms/SelectFormField';
import { TextFormField, TextFormFieldProps } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';

interface PhoneFormDoubleFieldProps<TFieldValues extends FieldValues> {
  dialCode?: {
    name: FieldPath<TFieldValues>;
    fieldProps?: Partial<SelectFormFieldProps<TFieldValues>>;
    gridProps?: GridProps;
  };
  phoneNumber?: {
    name: FieldPath<TFieldValues>;
    fieldProps?: Partial<TextFormFieldProps<TFieldValues>>;
    gridProps?: GridProps;
  };
  defaultValue?: string | null;
  availableCountriesIso2?: string[];
  fieldVariant?: FieldVariant;
  markAsOptional?: boolean;
  disabled?: boolean;
}

export function PhoneFormDoubleField<T extends FieldValues>({
  dialCode,
  phoneNumber,
  defaultValue,
  availableCountriesIso2,
  fieldVariant = FieldVariant.form,
  markAsOptional = false,
  disabled = false,
}: PhoneFormDoubleFieldProps<T>) {
  const optionList = getDialCodeList(availableCountriesIso2);
  const getInputDisplay = (option: Country) => (option.iso2 ? `${countryToFlag(option.iso2)} +${option.dialCode}` : '');
  const getListDisplay = (option: Country) =>
    option.dialCode ? `${countryToFlag(option.iso2)} +${option.dialCode} ${option.name}` : '-';
  const getSearchValue = (option: Country) => `+${option.dialCode} ${option.name}`;

  const defaultDialCode = {
    name: 'dialCode' as Path<T>,
    fieldProps: {
      placeholder: '',
      label: 'DIAL_CODE',
    },
  };

  const defaultPhoneNumber = {
    name: 'phoneNumber' as Path<T>,
    fieldProps: { label: 'PHONE_NUMBER' },
  };

  const dialCodeName = dialCode?.name ?? defaultDialCode.name;
  const dialCodeFieldProps = { ...defaultDialCode.fieldProps, ...dialCode?.fieldProps };

  const phoneNumberName = phoneNumber?.name ?? defaultPhoneNumber.name;
  const phoneNumberFieldProps = { ...defaultPhoneNumber.fieldProps, ...phoneNumber?.fieldProps };

  if (fieldVariant === FieldVariant.dataRow) {
    return <TextFormField label={phoneNumberFieldProps.label} name={phoneNumberName} value={defaultValue} />;
  }

  return (
    <Box display={'flex'} gridGap={'8px'}>
      <Box minWidth={90} maxWidth={120} {...dialCode?.gridProps}>
        <AutocompleteFormField
          disabled={disabled}
          name={dialCodeName}
          autocompleteProps={{ disableClearable: true }}
          optionList={optionList}
          getInputDisplay={getInputDisplay}
          getListDisplay={getListDisplay}
          getSearchValue={getSearchValue}
          fieldVariant={fieldVariant}
          markAsOptional={markAsOptional}
          {...dialCodeFieldProps}
          style={{
            minWidth: '90px',
            ...dialCode?.fieldProps?.style,
          }}
        />
      </Box>
      <Box flexGrow={1} {...phoneNumber?.gridProps}>
        <TextFormField
          disabled={disabled}
          name={phoneNumberName}
          fieldVariant={fieldVariant}
          markAsOptional={markAsOptional}
          onKeyDown={(event) => {
            // allow copy paste
            if (event.ctrlKey || event.metaKey) {
              return;
            }
            // remove alphabetic keys
            if (event?.key.length === 1 && event?.key.toLowerCase() != event?.key.toUpperCase()) {
              event.preventDefault();
            }
          }}
          {...phoneNumberFieldProps}
        />
      </Box>
    </Box>
  );
}
